import { Search } from '@mui/icons-material';
import { Chip, Tab, Tabs } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import React, { useMemo, useState } from 'react';

import { dateToString, DATE_FORMAT_ONLY_MONTH_AND_YEAR } from '../utilities/formattingUtilities';
import { formatLocation, valueToString } from './helpers';
import { DoctorRow, DoctorSource, DxClaim } from './types';

type Variant = 'dx' | 'rx';

const CountBadge = ({
  count,
  variant,
  active,
}: {
  count: number;
  variant: Variant;
  active: boolean;
}) => (
  <span className={`count-badge ${variant} ${active ? 'active' : ''}`}>
    {count}
  </span>
);

const TabLabel = ({
  label,
  count,
  variant,
  active,
}: {
  label: string;
  count: number;
  variant: Variant;
  active: boolean;
}) => (
  <span className="tab-label">
    {label}
    <CountBadge count={count} variant={variant} active={active} />
  </span>
);

const AlsoInPill = ({ source }: { source: DoctorSource }) => {
  const variant: Variant = source === 'Dx' ? 'dx' : 'rx';
  const label = source === 'Dx' ? 'Also in DX' : 'Also in Rx';
  return <span className={`also-in-pill ${variant}`}>{label}</span>;
};

interface AllDoctorsProps {
  /** Deduplicated DX rows, sorted by visit date (from `prepareAllDoctorsRows`). */
  dxRows: DxClaim[];
  /** RX rows sorted by visit date (from `prepareAllDoctorsRows`). */
  rxRows: DoctorRow[];
}

export default function AllDoctors({ dxRows, rxRows }: AllDoctorsProps) {
  const [activeTab, setActiveTab] = useState<DoctorSource>('Dx');

  const crossRefIds = useMemo(() => {
    const dxIds = new Set(dxRows.map(c => c.npi).filter(Boolean));
    const rxIds = new Set(rxRows.map(r => r.npi).filter(Boolean));
    const intersection = new Set<string>();
    dxIds.forEach(id => {
      if (rxIds.has(id)) intersection.add(id);
    });
    return intersection;
  }, [dxRows, rxRows]);

  const rows: (DxClaim | DoctorRow)[] = activeTab === 'Dx' ? dxRows : rxRows;
  const otherSource: DoctorSource = activeTab === 'Dx' ? 'Rx' : 'Dx';

  return (
    <>
      <div className="tabs-bar">
        <Tabs
          value={activeTab}
          onChange={(_e, value: DoctorSource) => setActiveTab(value)}
          aria-label="Doctor source tabs"
        >
          <Tab
            value="Dx"
            label={
              <TabLabel
                label="DX"
                count={dxRows.length}
                variant="dx"
                active={activeTab === 'Dx'}
              />
            }
          />
          <Tab
            value="Rx"
            label={
              <TabLabel
                label="Rx"
                count={rxRows.length}
                variant="rx"
                active={activeTab === 'Rx'}
              />
            }
          />
        </Tabs>
      </div>

      <Table
        aria-label="Doctor info table"
        size="small"
        className="doctor-table"
      >
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Specialization</TableCell>
            <TableCell>Last Visit Date</TableCell>
            <TableCell>NPI</TableCell>
            <TableCell>Phone</TableCell>
            <TableCell>Location</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.length > 0 ? (
            rows.map((row, index) => {
              const alsoInOther = !!row.npi && crossRefIds.has(row.npi);
              return (
                <TableRow key={`${row.npi || row.name}-${index}`}>
                  <TableCell>
                    <div className="name-cell">
                      {valueToString(row.name)}
                      {alsoInOther && <AlsoInPill source={otherSource} />}
                    </div>
                  </TableCell>
                  <TableCell>
                    {row.specialization ? (
                      <Chip
                        label={row.specialization}
                        size="small"
                        className="spec-chip"
                      />
                    ) : (
                      valueToString(undefined)
                    )}
                  </TableCell>
                  <TableCell>{dateToString(row.visitDate, DATE_FORMAT_ONLY_MONTH_AND_YEAR)}</TableCell>
                  <TableCell>
                    {row.npi ? (
                      <span className="npi">{row.npi}</span>
                    ) : (
                      valueToString(undefined)
                    )}
                  </TableCell>
                  <TableCell>{valueToString(row.phoneNumber)}</TableCell>
                  <TableCell>
                    {valueToString(
                      formatLocation(
                        row.address,
                        row.city,
                        row.state,
                        row.zipcode,
                      ),
                    )}
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <div className="no-data-found-container">
                  <Search />
                  <p>No data found</p>
                </div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </>
  );
}
